import { fetchEventSource } from "@microsoft/fetch-event-source";
async function authHeaders(getToken) {
    const headers = { "Content-Type": "application/json" };
    const token = await getToken?.();
    if (token)
        headers["Authorization"] = `Bearer ${token}`;
    return headers;
}
const DEFAULT_BASE_URL = "/api/copilot";
// ─── REST calls ─────────────────────────────────────────────────────
export async function listAgents(options) {
    const base = options?.baseUrl ?? DEFAULT_BASE_URL;
    const res = await fetch(`${base}/agents`, { headers: await authHeaders(options?.getToken) });
    if (!res.ok)
        throw new Error(`Failed to list agents: ${res.statusText}`);
    const data = (await res.json());
    return data.agents;
}
export async function listModels(options) {
    const base = options?.baseUrl ?? DEFAULT_BASE_URL;
    const res = await fetch(`${base}/models`, { headers: await authHeaders(options?.getToken) });
    if (!res.ok)
        throw new Error(`Failed to list models: ${res.statusText}`);
    const data = (await res.json());
    return data.models;
}
/**
 * Create a new thread and run the first turn. Streams SSE.
 *
 * The first event is `run_status { status: "thread_created", threadId }`
 * carrying the new id (UI must capture it for subsequent calls).
 */
export async function createThread(req, callbacks, options) {
    return openSseStream(threadsUrl(options), req, callbacks, options);
}
/**
 * Resume an existing thread with a new user message. Streams SSE.
 * AFD Rules Engine pins this to the right region by URL prefix.
 */
export async function sendMessage(threadId, req, callbacks, options) {
    const url = `${threadsUrl(options)}/${encodeURIComponent(threadId)}/messages`;
    return openSseStream(url, req, callbacks, options);
}
/**
 * Resolve pending confirmations on a thread. Streams SSE for the
 * continuation that follows.
 */
export async function confirmThread(threadId, req, callbacks, options) {
    const url = `${threadsUrl(options)}/${encodeURIComponent(threadId)}/confirm`;
    return openSseStream(url, req, callbacks, options);
}
function threadsUrl(options) {
    const base = options?.baseUrl ?? DEFAULT_BASE_URL;
    return `${base}/threads`;
}
async function openSseStream(url, body, callbacks, options) {
    const headers = await authHeaders(options?.getToken);
    return fetchEventSource(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: callbacks.signal,
        async onopen(response) {
            if (!response.ok) {
                throw new Error(`Stream open failed: ${response.status} ${response.statusText}`);
            }
            const contentType = response.headers.get("content-type") ?? "";
            if (!contentType.includes("text/event-stream")) {
                throw new Error(`Unexpected content-type: ${contentType || "(missing)"}`);
            }
        },
        onmessage(msg) {
            try {
                const data = JSON.parse(msg.data);
                const eventType = msg.event || "message_delta";
                callbacks.onEvent(decodeWireEvent(eventType, data));
            }
            catch {
                // ignore malformed events
            }
        },
        onerror(err) {
            callbacks.onError?.(err);
            throw err;
        },
        onclose() {
            callbacks.onClose?.();
        },
        openWhenHidden: true,
    });
}
const wireEventTypeMap = {
    md: "message_delta",
    th: "thinking",
    tcs: "tool_call_start",
    tcr: "tool_call_result",
    cr: "confirmation_required",
    ir: "input_required",
    ar: "auth_required",
    rs: "run_status",
    er: "error",
    dn: "done",
};
function decodeWireEvent(eventType, data) {
    const mapped = wireEventTypeMap[eventType];
    if (!mapped) {
        return { event: eventType, data };
    }
    switch (mapped) {
        case "message_delta":
        case "thinking":
            return { event: mapped, data: { content: data.c } };
        case "tool_call_start":
            return {
                event: mapped,
                data: {
                    toolCallId: data.id,
                    toolName: data.n,
                    arguments: data.a,
                },
            };
        case "tool_call_result":
            return {
                event: mapped,
                data: {
                    toolCallId: data.id,
                    toolName: data.n,
                    result: data.r,
                },
            };
        case "confirmation_required":
            return {
                event: mapped,
                data: {
                    threadId: data.t,
                    runId: data.rid,
                    pendingToolCalls: Array.isArray(data.pt)
                        ? data.pt.map((ptc) => ({
                            toolCallId: ptc.id,
                            toolName: ptc.n,
                            description: ptc.d,
                            arguments: ptc.a,
                        }))
                        : [],
                },
            };
        case "input_required":
            return {
                event: mapped,
                data: {
                    threadId: data.t,
                    runId: data.rid,
                    requests: Array.isArray(data.rq)
                        ? data.rq.map((r) => ({
                            toolCallId: r.id,
                            toolName: r.n,
                            questions: r.q ?? [],
                        }))
                        : [],
                },
            };
        case "auth_required":
            return {
                event: mapped,
                data: {
                    provider: data.p,
                    authorizeUrl: data.u,
                    message: data.m,
                },
            };
        case "run_status":
            return {
                event: mapped,
                data: {
                    status: data.s,
                    runId: data.rid,
                    // `threadId` is set on the create stream's first event
                    // (`status: "thread_created"`). Other run_status payloads
                    // omit it; callers should default to the existing threadId.
                    threadId: data.t,
                },
            };
        case "error":
            return {
                event: mapped,
                data: {
                    message: data.m,
                    code: data.cd,
                },
            };
        case "done":
            return {
                event: mapped,
                data: {
                    threadId: data.t,
                    pendingConfirmation: data.pc,
                },
            };
    }
}
//# sourceMappingURL=api.js.map